import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, User, MessageSquare, Send, Headphones, X, UserX } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import SupportAttachment from "@/components/SupportAttachment";
import AttachmentPreview from "@/components/AttachmentPreview";

interface GuestMessage {
  id: string;
  session_id: string;
  message: string;
  role: string;
  created_at: string;
  admin_id: string | null;
  admin_name?: string | null;
  admin_email?: string | null;
  attachment_url?: string | null;
  attachment_type?: string | null;
}

interface GuestRequest {
  id: string;
  session_id: string;
  status: string;
  created_at: string;
  guest_info: unknown;
}

interface GroupedGuestChat {
  session_id: string;
  messages: GuestMessage[];
  lastMessageAt: string;
  request: GuestRequest | null;
}

const PUBLIC_SUPPORT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-support`;

const AdminGuestSupport = () => {
  const { user: adminUser, session } = useAuth();
  const { toast } = useToast();
  const [groupedChats, setGroupedChats] = useState<GroupedGuestChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState<{ url: string; type: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    if (!session?.access_token) return;

    try {
      // Fetch guest requests
      const { data: requests, error: requestsError } = await supabase
        .from("guest_support_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (requestsError) throw requestsError;

      // Fetch all guest messages
      const { data: messages, error: messagesError } = await supabase
        .from("guest_messages")
        .select("*")
        .order("created_at", { ascending: true });

      if (messagesError) throw messagesError;

      // Fetch admin profiles for messages with admin_id
      const adminIds = [...new Set((messages || []).filter(m => m.admin_id).map(m => m.admin_id))];
      let adminProfiles: Record<string, { full_name: string | null; email: string }> = {};
      
      if (adminIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("user_id, full_name, email")
          .in("user_id", adminIds);
        
        if (profilesData) {
          adminProfiles = profilesData.reduce((acc, p) => {
            acc[p.user_id] = { full_name: p.full_name, email: p.email };
            return acc;
          }, {} as Record<string, { full_name: string | null; email: string }>);
        }
      }

      // Enrich messages with admin info
      const enrichedMessages = (messages || []).map(msg => ({
        ...msg,
        admin_name: msg.admin_id ? adminProfiles[msg.admin_id]?.full_name : null,
        admin_email: msg.admin_id ? adminProfiles[msg.admin_id]?.email : null,
      }));

      // Group by session
      const grouped: Record<string, GuestMessage[]> = {};
      enrichedMessages.forEach((msg) => {
        if (!grouped[msg.session_id]) {
          grouped[msg.session_id] = [];
        }
        grouped[msg.session_id].push(msg);
      });

      // Also include sessions from requests that might not have messages yet
      (requests || []).forEach((req) => {
        if (!grouped[req.session_id]) {
          grouped[req.session_id] = [];
        }
      });

      const groupedArray: GroupedGuestChat[] = Object.entries(grouped).map(([session_id, msgs]) => {
        const request = (requests || []).find((r) => r.session_id === session_id);
        return {
          session_id,
          messages: msgs,
          lastMessageAt: msgs.length > 0 ? msgs[msgs.length - 1].created_at : request?.created_at || "",
          request: request || null,
        };
      });

      // Sort: pending first, then by date
      groupedArray.sort((a, b) => {
        if (a.request?.status === "pending" && b.request?.status !== "pending") return -1;
        if (b.request?.status === "pending" && a.request?.status !== "pending") return 1;
        if (a.request && !b.request) return -1;
        if (b.request && !a.request) return 1;
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      });

      setGroupedChats(groupedArray);
    } catch (error) {
      console.error("Error fetching guest data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Set up realtime
    const channel = supabase
      .channel("admin-guest-support")
      .on("postgres_changes", { event: "*", schema: "public", table: "guest_messages" }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "guest_support_requests" }, () => fetchData())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.access_token]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedSessionId, groupedChats]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSendReply = async () => {
    if (!selectedSessionId || (!replyMessage.trim() && !attachment) || !session?.access_token) return;

    setSending(true);
    try {
      const response = await fetch(PUBLIC_SUPPORT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: "admin_reply",
          sessionId: selectedSessionId,
          message: replyMessage,
          attachmentUrl: attachment?.url,
          attachmentType: attachment?.type,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send reply");
      }

      setReplyMessage("");
      setAttachment(null);
      toast({
        title: "تم الإرسال",
        description: "تم إرسال الرد للزائر",
      });
      fetchData();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast({
        title: "خطأ",
        description: "فشل في إرسال الرد",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentUpload = (url: string, type: string) => {
    if (url) {
      setAttachment({ url, type });
    } else {
      setAttachment(null);
    }
  };

  const handleCloseRequest = async (sessionId: string) => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(PUBLIC_SUPPORT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: "admin_close",
          sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to close request");
      }

      toast({
        title: "تم الإغلاق",
        description: "تم إغلاق طلب دعم الزائر",
      });
      fetchData();
    } catch (error) {
      console.error("Error closing request:", error);
    }
  };

  const selectedChat = groupedChats.find((g) => g.session_id === selectedSessionId);
  const pendingCount = groupedChats.filter((g) => g.request?.status === "pending").length;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Sessions List */}
      <Card className="glass border-border lg:col-span-1">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <UserX className="w-5 h-5" />
            زوار ({groupedChats.length})
            {pendingCount > 0 && (
              <Badge variant="destructive" className="animate-pulse">
                {pendingCount} جديد
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {groupedChats.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground px-4">
              لا توجد طلبات دعم من الزوار
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-border">
                {groupedChats.map((chat) => {
                  const hasPending = chat.request?.status === "pending";
                  const hasActive = chat.request?.status === "active";
                  const userMsgsCount = chat.messages.filter((m) => m.role === "user").length;

                  return (
                    <button
                      key={chat.session_id}
                      onClick={() => setSelectedSessionId(chat.session_id)}
                      className={`w-full p-4 text-right hover:bg-secondary/50 transition-colors ${
                        selectedSessionId === chat.session_id ? "bg-secondary" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          {hasPending && (
                            <Badge variant="destructive" className="animate-pulse">
                              <Headphones className="w-3 h-3 ml-1" />
                              جديد
                            </Badge>
                          )}
                          {hasActive && (
                            <Badge variant="default" className="bg-green-600">
                              نشط
                            </Badge>
                          )}
                          <Badge variant="secondary">{userMsgsCount} رسالة</Badge>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <UserX className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 font-mono truncate">
                        {chat.session_id.slice(0, 20)}...
                      </p>
                      {chat.lastMessageAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          آخر نشاط: {formatDate(chat.lastMessageAt)}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Messages View */}
      <Card className="glass border-border lg:col-span-2 flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-foreground">
            {selectedSessionId ? "محادثة الزائر" : "اختر زائر لعرض المحادثة"}
          </CardTitle>
          {selectedChat?.request && selectedChat.request.status !== "closed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCloseRequest(selectedChat.session_id)}
              className="text-destructive"
            >
              <X className="w-4 h-4 ml-2" />
              إغلاق
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          {!selectedSessionId ? (
            <div className="text-center py-20 text-muted-foreground flex-1 flex flex-col items-center justify-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>اختر زائر من القائمة لعرض الرسائل</p>
            </div>
          ) : selectedChat ? (
            <>
              <ScrollArea className="flex-1 h-[400px] pr-4" ref={scrollRef}>
                <div className="space-y-4">
                  {selectedChat.messages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      لا توجد رسائل بعد
                    </div>
                  ) : (
                    selectedChat.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        {msg.role !== "user" && (
                          <div className="w-8 h-8 rounded-full bg-green-600/20 flex items-center justify-center flex-shrink-0">
                            <Headphones className="w-4 h-4 text-green-600" />
                          </div>
                        )}
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                            msg.role === "user"
                              ? "bg-muted text-foreground"
                              : "bg-green-600/20 text-foreground border border-green-600/30"
                          }`}
                        >
                          {msg.role !== "user" && (
                            <div className="mb-1">
                              <p className="text-xs text-green-600 font-medium">
                                {msg.admin_name || "فريق الدعم"}
                              </p>
                              {msg.admin_email && (
                                <p className="text-xs text-muted-foreground">{msg.admin_email}</p>
                              )}
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          {msg.attachment_url && msg.attachment_type && (
                            <AttachmentPreview url={msg.attachment_url} type={msg.attachment_type} />
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                        {msg.role === "user" && (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <UserX className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>

              {/* Reply Input */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-border items-center">
                <SupportAttachment 
                  onUpload={handleAttachmentUpload}
                  disabled={sending}
                />
                <Input
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="اكتب ردك للزائر..."
                  disabled={sending}
                  className="flex-1"
                  dir="rtl"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
                <Button onClick={handleSendReply} disabled={sending || (!replyMessage.trim() && !attachment)}>
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGuestSupport;
